import { Head, router, useForm } from "@inertiajs/react"
import { UserPlus, Users, Search, Trash2, Shield, Pencil, Loader2, Plus, Settings } from "lucide-react"
import { useState, useMemo, useId, useEffect } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { SectionTabs } from "../components/SectionTabs"
import { AddMemberDialog } from "../components/AddMemberDialog"
import { CreateGroupDialog } from "../components/CreateGroupDialog"
import { EditMemberDialog } from "../components/EditMemberDialog"
import { RemoveMemberConfirm } from "../components/RemoveMemberConfirm"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

const userManagementTabs = [
  { label: "Users", href: "/app/coach/users" },
  { label: "Groups", href: "/app/coach/group" },
  { label: "Prospects", href: "/app/coach/prospects" },
]

function RoleBadge({ role }) {
  if (role === "coach") {
    return <Badge className="bg-kf-red/10 text-kf-red border-kf-red/20 hover:bg-kf-red/10"><Shield className="h-3 w-3 mr-1" />Coach</Badge>
  }
  return <Badge variant="secondary">Member</Badge>
}

// One row on a group's Settings tab: the label and a plain-language
// description on the left, the control on the right (stacked on a phone).
function SettingRow({ label, htmlFor, description, children }) {
  return (
    <div className="flex flex-col gap-2 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6 sm:px-6">
      <div className="min-w-0 max-w-xl">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="text-sm font-medium text-zinc-900">{label}</label>
        ) : (
          <p className="text-sm font-medium text-zinc-900">{label}</p>
        )}
        <p className="mt-0.5 text-xs leading-snug text-zinc-500">{description}</p>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}

// Settings PATCHes keep page state so the card stays on its Settings tab
// after the round-trip re-renders the page.
const SETTINGS_VISIT = { preserveScroll: true, preserveState: true }

const CADENCE_OPTIONS = [
  { value: "none", label: "Off (no reminders)" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
]

const SELECT_CLASS =
  "rounded border border-zinc-200 bg-white py-1.5 pl-2 pr-8 text-sm text-zinc-700 hover:border-zinc-300"

// The group's assessment-reminder cadence. Soft setting -- drives the
// dashboard banner only; never blocks members from anything.
function CadenceSettingForm({ group }) {
  const selectId = useId()
  const onChange = (e) => {
    const value = e.target.value
    if (value === group.assessmentReminderCadence) return
    router.patch(
      `/app/coach/group/settings/${group.id}`,
      { assessment_reminder_cadence: value },
      SETTINGS_VISIT,
    )
  }

  return (
    <SettingRow
      label="Assessment reminder"
      htmlFor={selectId}
      description="How often members see a nudge on their dashboard to revisit their life assessment (Integrated Life Audit). This is the group default — the Reminders switch on each member row turns it off for one member. Members can take or retake the assessment anytime."
    >
      <select
        id={selectId}
        defaultValue={group.assessmentReminderCadence || "none"}
        onChange={onChange}
        className={SELECT_CLASS}
      >
        {CADENCE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </SettingRow>
  )
}

// The four coach-controlled assessment access levels.
const ACCESS_LEVEL_OPTIONS = [
  { value: "none", label: "None" },
  { value: "wheel_only", label: "Integrated Life Audit only" },
  { value: "plan_only", label: "Plan only" },
  { value: "full", label: "Full" },
]

// The group's assessment access LEVEL. Controls which assessment surfaces
// members see — None (group hub only), Integrated Life Audit only, Plan only,
// or Full. Distinct from the reminder cadence above (which only nudges).
function GroupAccessLevelForm({ group }) {
  const id = useId()
  return (
    <SettingRow
      label="Assessment access"
      htmlFor={id}
      description="Which assessment surfaces members see. None shows just the group hub; Integrated Life Audit only opens the audit; Plan only opens the one-year plan; Full opens everything. Members can't tell a hidden surface exists."
    >
      <select
        id={id}
        value={group.assessmentAccess || "none"}
        onChange={(e) =>
          router.patch(
            `/app/coach/group/assessment_access/${group.id}`,
            { assessment_access: e.target.value },
            SETTINGS_VISIT,
          )
        }
        className={SELECT_CLASS}
        aria-label={`Assessment access for members of ${group.name}`}
      >
        {ACCESS_LEVEL_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </SettingRow>
  )
}

// Multi-select of member-portal sections hidden for this group's members
// (task #333) — a SEPARATE axis from assessment access (R8). A checkbox
// popover, not the single <select> above, because hiding is a multi-select
// of independent tabs rather than a mutually exclusive level. Registry-driven
// (`memberSections` comes from the server) so the frontend never hard-codes
// section names (KTD3).
function HiddenSectionsForm({ group, memberSections }) {
  const hidden = new Set(group.hiddenMemberTabs || [])

  const toggle = (key) => {
    const next = new Set(hidden)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    router.patch(
      `/app/coach/group/hidden_sections/${group.id}`,
      { hidden_member_tabs: Array.from(next) },
      SETTINGS_VISIT,
    )
  }

  return (
    <SettingRow
      label="Hidden sections"
      description="Removes a section from this group's members entirely — from their sidebar and from the page itself. Members can't tell a hidden section exists. Home is always visible."
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="font-normal text-zinc-700"
          >
            {hidden.size > 0 ? `${hidden.size} hidden` : "None hidden"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-3">
          <p className="text-xs font-medium text-zinc-900 mb-2">
            Hide sections for {group.name}
          </p>
          <div className="space-y-2">
            {memberSections.map((section) => (
              <label
                key={section.key}
                className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-zinc-300 text-kf-red focus:ring-kf-red cursor-pointer"
                  checked={hidden.has(section.key)}
                  onChange={() => toggle(section.key)}
                  aria-label={`Hide ${section.label} for ${group.name}`}
                />
                {section.label}
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </SettingRow>
  )
}

// An on/off setting. Controlled, non-optimistic like the reminders switch:
// it reflects props and only flips when the PATCH round-trip re-renders.
function SwitchSetting({ label, description, checked, onCheckedChange, ariaLabel }) {
  return (
    <SettingRow label={label} description={description}>
      <div className="inline-flex items-center gap-2 text-sm text-zinc-600">
        <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={ariaLabel} />
        <span className="w-6">{checked ? "On" : "Off"}</span>
      </div>
    </SettingRow>
  )
}

// Member directory switch (#651). Off by default; on, this group's members get
// a Directory tab and the group is listed in its sister groups' directories.
function MemberDirectoryForm({ group }) {
  return (
    <SwitchSetting
      label="Member directory"
      description="On, members get a Directory tab. Members of your other groups with the directory on can see this group's names and contact details."
      checked={!!group.memberDirectoryEnabled}
      onCheckedChange={(checked) => {
        router.patch(
          `/app/coach/group/member_directory/${group.id}`,
          { member_directory_enabled: checked },
          SETTINGS_VISIT,
        )
      }}
      ariaLabel={`Member directory for ${group.name}`}
    />
  )
}

// Group replies switch (#683). On by default (the opposite polarity to the
// member directory); on, a reply to a Group email (prayer request, Godly
// Council, Imperative) reaches the author and the Group, off the author only.
function GroupRepliesForm({ group }) {
  return (
    <SwitchSetting
      label="Group replies"
      description="On, a reply to a group email (prayer request, Godly Council, or imperative) reaches the author and the whole group. Off, it reaches the author only."
      checked={!!group.groupRepliesEnabled}
      onCheckedChange={(checked) => {
        router.patch(
          `/app/coach/group/group_replies/${group.id}`,
          { group_replies_enabled: checked },
          SETTINGS_VISIT,
        )
      }}
      ariaLabel={`Group replies for ${group.name}`}
    />
  )
}

// Godly Council and Imperative email switches (#683 follow-up). Off by
// default, so each coach turns them on; on, a new one emails the rest of the
// group.
function GodlyCouncilEmailsForm({ group }) {
  return (
    <SwitchSetting
      label="Godly Council emails"
      description="On, the group gets an email when someone asks for counsel. A reply follows the Group replies setting."
      checked={!!group.godlyCouncilEmailsEnabled}
      onCheckedChange={(checked) => {
        router.patch(
          `/app/coach/group/godly_council_emails/${group.id}`,
          { godly_council_emails_enabled: checked },
          SETTINGS_VISIT,
        )
      }}
      ariaLabel={`Godly Council emails for ${group.name}`}
    />
  )
}

function ImperativeEmailsForm({ group }) {
  return (
    <SwitchSetting
      label="Imperative emails"
      description="On, the group gets an email when someone makes a new imperative. A reply follows the Group replies setting."
      checked={!!group.imperativeEmailsEnabled}
      onCheckedChange={(checked) => {
        router.patch(
          `/app/coach/group/imperative_emails/${group.id}`,
          { imperative_emails_enabled: checked },
          SETTINGS_VISIT,
        )
      }}
      ariaLabel={`Imperative emails for ${group.name}`}
    />
  )
}

// Everything on a group's Settings tab, one divided row per setting.
function GroupSettings({ group, memberSections }) {
  return (
    <div className="divide-y divide-zinc-100">
      <GroupAccessLevelForm group={group} />
      <CadenceSettingForm group={group} />
      <HiddenSectionsForm group={group} memberSections={memberSections} />
      <MemberDirectoryForm group={group} />
      <GroupRepliesForm group={group} />
      <GodlyCouncilEmailsForm group={group} />
      <ImperativeEmailsForm group={group} />
    </div>
  )
}

// Single-field rename for a coached group. Mounted persistently on the page;
// `group` (null when closed) both controls open state and seeds the field.
// Re-seeds whenever a different group opens, mirroring EditMemberDialog.
function RenameGroupDialog({ group, onClose }) {
  const form = useForm({ name: "" })
  const groupId = group?.id

  useEffect(() => {
    if (group) {
      form.setData("name", group.name ?? "")
      form.clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!group || !form.data.name.trim()) return
    form.patch(`/app/coach/group/name/${group.id}`, {
      preserveScroll: true,
      onSuccess: () => onClose(),
    })
  }

  return (
    <ResponsiveDialog open={!!group} onOpenChange={(o) => { if (!o) onClose() }}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>Rename group</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Update the name members see for this group.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-1.5 px-4 py-4 sm:px-0">
            <Label htmlFor="rename-group-name">Group name</Label>
            <Input
              id="rename-group-name"
              value={form.data.name}
              onChange={(e) => form.setData("name", e.target.value)}
              placeholder="e.g. Cohort Alpha"
              autoFocus
            />
            {form.errors.name && <p className="text-xs text-red-600">{form.errors.name}</p>}
          </div>

          <ResponsiveDialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing || !form.data.name.trim()}
            >
              {form.processing && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Save Changes
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default function GroupShow({
  currentUser,
  coachGroup,
  coachGroups,
  coachedGroups = [],
  searchPool = [],
  memberSections = [],
}) {
  const [addOpen, setAddOpen] = useState(false)
  const [addMemberDefaultGroupId, setAddMemberDefaultGroupId] = useState(null)
  const [pendingRemove, setPendingRemove] = useState(null)
  const [pendingEdit, setPendingEdit] = useState(null)
  const [renaming, setRenaming] = useState(null)
  const [createGroupOpen, setCreateGroupOpen] = useState(false)
  const [search, setSearch] = useState("")

  const filteredCoachedGroups = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return coachedGroups
    return coachedGroups
      .map((g) => ({
        ...g,
        members: g.members.filter(
          (m) =>
            m.name?.toLowerCase().includes(q) ||
            m.email?.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.members.length > 0)
  }, [search, coachedGroups])

  const openAddMember = (groupId) => {
    setAddMemberDefaultGroupId(groupId)
    setAddOpen(true)
  }

  // onSuccess, never onFinish: onFinish fires on failure too, so a refused
  // removal or transfer would close the dialog as if it had saved — exactly
  // the silent-failure bug fixed in task #335. The server sends inertia
  // errors on those branches; the dialog has to stay open to show them.
  const handleRemove = (membershipId) => {
    router.delete(`/app/coach/group/members/${membershipId}`, {
      onSuccess: () => setPendingRemove(null),
    })
  }

  const handleTransfer = (membershipId, targetGroupId) => {
    router.patch(
      `/app/coach/group/members/${membershipId}/transfer`,
      { group_id: targetGroupId },
      { onSuccess: () => setPendingRemove(null) }
    )
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="Groups - Kingdom Factor" />

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Groups</h1>
            <p className="text-zinc-600 mt-1">
              Members in each group you coach. Add or invite people to a specific group;
              removing someone only takes them out of that group.
            </p>
          </div>
          <Button
            variant="gradient"
            className="shrink-0 w-full sm:w-auto"
            onClick={() => setCreateGroupOpen(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New group
          </Button>
        </div>

        <SectionTabs tabs={userManagementTabs} />

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            placeholder="Search members across groups..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {coachedGroups.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium">No groups assigned</p>
            <p className="text-sm">Contact an admin if you expect to coach a group.</p>
          </div>
        ) : filteredCoachedGroups.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium">No matches</p>
            <p className="text-sm">Try a different search.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredCoachedGroups.map((group) => (
              <Card key={group.id} className="py-0 gap-0 overflow-hidden shadow-sm">
                <Tabs defaultValue="members" className="gap-0">
                <div className="flex flex-col gap-3 border-b border-zinc-200 px-4 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-6">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-zinc-900">{group.name}</h2>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 shrink-0 p-0 text-zinc-400 hover:text-kf-red"
                        onClick={() => setRenaming(group)}
                        title="Rename group"
                        aria-label={`Rename ${group.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <TabsList aria-label={`${group.name} sections`}>
                      <TabsTrigger value="members" className="px-3">
                        <Users aria-hidden />
                        Members ({group.members.length})
                      </TabsTrigger>
                      <TabsTrigger value="settings" className="px-3">
                        <Settings aria-hidden />
                        Settings
                      </TabsTrigger>
                    </TabsList>
                  </div>
                  <Button
                    variant="gradient"
                    size="sm"
                    className="shrink-0 w-full sm:w-auto"
                    onClick={() => openAddMember(group.id)}
                  >
                    <UserPlus className="h-4 w-4 mr-1.5" />
                    Add member
                  </Button>
                </div>
                <TabsContent value="settings">
                  <GroupSettings group={group} memberSections={memberSections} />
                </TabsContent>
                <TabsContent value="members">
                {group.members.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-zinc-500 sm:px-6">
                    {search.trim()
                      ? "No members match your search in this group."
                      : "No members yet. Click Add member to invite someone."}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead className="hidden sm:table-cell">Email</TableHead>
                          <TableHead className="w-28">Role</TableHead>
                          <TableHead className="hidden sm:table-cell w-28">Reminders</TableHead>
                          <TableHead className="hidden md:table-cell w-36">Joined</TableHead>
                          <TableHead className="w-32 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.members.map((m) => {
                          const canEditMember = m.role !== "coach"
                          // The wheel-retake reminder only fires for wheel-
                          // accessible members, so it's moot when the group's
                          // level hides the wheel (none / plan_only).
                          const wheelHidden =
                            group.assessmentAccess === "none" || group.assessmentAccess === "plan_only"
                          const detailHref = `/app/coach/members/${m.accountId}?back=group`
                          const navigate = canEditMember ? () => router.visit(detailHref) : undefined
                          return (
                            <TableRow
                              key={m.id}
                              className={
                                canEditMember
                                  ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                  : undefined
                              }
                              tabIndex={canEditMember ? 0 : undefined}
                              aria-label={
                                canEditMember ? `View ${m.name || "member"}` : undefined
                              }
                              onClick={navigate}
                              onKeyDown={
                                canEditMember
                                  ? (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault()
                                        navigate()
                                      }
                                    }
                                  : undefined
                              }
                            >
                              <TableCell className="whitespace-normal">
                                <span className="font-medium text-zinc-900">{m.name}</span>
                                {m.status === "unverified" && (
                                  <Badge className="ml-2 bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200">
                                    Pending invite
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="hidden sm:table-cell text-sm text-zinc-600">{m.email}</TableCell>
                              <TableCell><RoleBadge role={m.role} /></TableCell>
                              <TableCell
                                className="hidden sm:table-cell"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") e.stopPropagation()
                                }}
                              >
                                {m.role === "coach" ? (
                                  <span className="text-xs text-zinc-400">—</span>
                                ) : wheelHidden ? (
                                  /* The wheel reminder is moot when the group's
                                     level hides the Integrated Life Audit — no banner to nudge. */
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex" tabIndex={0}>
                                          <Switch
                                            checked={false}
                                            disabled
                                            aria-label={`Reminders unavailable — Integrated Life Audit hidden for ${m.name || "member"}`}
                                          />
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-xs text-left leading-snug">
                                        Integrated Life Audit hidden — reminders don&apos;t apply.
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  /* Controlled, non-optimistic: the switch reflects props and
                                     only flips when the PATCH round-trip re-renders the page. */
                                  <Switch
                                    checked={m.assessmentRemindersEnabled}
                                    onCheckedChange={(checked) => {
                                      router.patch(
                                        `/app/coach/group/members/${m.id}/reminders`,
                                        { assessment_reminders_enabled: checked },
                                        { preserveScroll: true },
                                      )
                                    }}
                                    aria-label={`Toggle assessment reminders for ${m.name || "member"}`}
                                  />
                                )}
                              </TableCell>
                              <TableCell className="hidden md:table-cell text-sm text-zinc-500">
                                {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : ""}
                              </TableCell>
                              <TableCell className="text-right">
                                {m.role === "coach" ? (
                                  <span className="text-xs text-zinc-400">—</span>
                                ) : (
                                  <div
                                    className="flex justify-end gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") e.stopPropagation()
                                    }}
                                  >
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-zinc-500 hover:text-kf-red"
                                      onClick={() => setPendingEdit(m)}
                                      title="Edit profile"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-zinc-500 hover:text-red-600"
                                      onClick={() => setPendingRemove(m)}
                                      title="Remove from this group"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
                </TabsContent>
                </Tabs>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AddMemberDialog
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open)
          if (!open) setAddMemberDefaultGroupId(null)
        }}
        searchPool={searchPool}
        groups={coachGroups || []}
        defaultGroupId={addMemberDefaultGroupId ?? coachGroup?.id}
      />

      <RemoveMemberConfirm
        member={pendingRemove}
        onCancel={() => setPendingRemove(null)}
        onConfirm={handleRemove}
        onTransfer={handleTransfer}
        coachedGroups={coachedGroups}
        groupName={pendingRemove?.groupName}
      />

      <EditMemberDialog
        member={pendingEdit}
        onClose={() => setPendingEdit(null)}
      />

      <RenameGroupDialog
        group={renaming}
        onClose={() => setRenaming(null)}
      />

      <CreateGroupDialog
        open={createGroupOpen}
        onOpenChange={setCreateGroupOpen}
      />
    </CoachLayout>
  )
}
